import { useEffect } from 'react'
import { View, Text } from 'react-native'
import { Pressable } from './Pressable'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  withRepeat,
  withSequence,
  interpolateColor,
  FadeInDown,
  FadeOut,
} from 'react-native-reanimated'
import { Check } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { COLORS, FONTS } from '../../lib/brand'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, DURATION, INTERACTION } from '../../lib/theme/tokens'
import { useAnimatedPress } from '../../lib/hooks/use-animated-press'
import { cn } from '../../lib/utils/cn'

interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: React.ReactNode
  required?: boolean
  error?: string
  disabled?: boolean
  compact?: boolean
  testID?: string
  accessibilityLabel?: string
  accessibilityHidden?: boolean
}

export function Checkbox({
  checked,
  onChange,
  label,
  required,
  error,
  disabled,
  compact,
  testID,
  accessibilityLabel,
  accessibilityHidden,
}: CheckboxProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const borderUnchecked = colors.textMuted

  const {
    animatedStyle: scaleStyle,
    onPressIn,
    onPressOut,
  } = useAnimatedPress({
    scaleTo: 0.98,
    haptic: false,
    disabled,
  })

  // Checkmark scale animation
  const checkScale = useSharedValue(checked ? 1 : 0)

  useEffect(() => {
    checkScale.value = checked
      ? withSpring(1, { damping: 12, stiffness: 200 })
      : withTiming(0, { duration: DURATION.fast })
  }, [checked, checkScale])

  const checkmarkStyle = useAnimatedStyle(() => ({
    transform: [{ scale: checkScale.value }],
    opacity: checkScale.value,
  }))

  // Box fill animation
  const fillProgress = useSharedValue(checked ? 1 : 0)

  useEffect(() => {
    fillProgress.value = withTiming(checked ? 1 : 0, { duration: DURATION.fast })
  }, [checked, fillProgress])

  const boxStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      fillProgress.value,
      [0, 1],
      ['transparent', COLORS.coral.DEFAULT],
    ),
    borderColor: interpolateColor(
      fillProgress.value,
      [0, 1],
      [borderUnchecked, COLORS.coral.DEFAULT],
    ),
  }))

  // Error pulse on unchecked
  const errorPulse = useSharedValue(1)

  useEffect(() => {
    if (error && !checked) {
      errorPulse.value = withRepeat(
        withSequence(
          withTiming(1, { duration: DURATION.slow }),
          withTiming(0.4, { duration: DURATION.slow }),
        ),
        -1,
        true,
      )
    } else {
      errorPulse.value = withTiming(1, { duration: DURATION.fast })
    }
  }, [error, checked, errorPulse])

  const errorBorderStyle = useAnimatedStyle(() => {
    if (!error || checked) return {}
    return {
      borderColor: interpolateColor(
        errorPulse.value,
        [0.4, 1],
        ['rgba(239,78,74,0.4)', COLORS.coral.DEFAULT],
      ),
    }
  })

  const handlePress = () => {
    if (disabled) return
    void selection()
    onChange(!checked)
  }

  const resolvedAccessibilityLabel =
    accessibilityLabel ?? (typeof label === 'string' && label.trim().length > 0 ? label : undefined)

  const content = (
    <>
      <Animated.View
        style={[
          boxStyle,
          error && !checked ? errorBorderStyle : undefined,
          {
            width: 20,
            height: 20,
            borderRadius: 5, // non-standard: visual checkbox rounding
            borderWidth: 2,
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 1,
            marginRight: SPACING.sm + 2,
            flexShrink: 0,
          },
        ]}
      >
        <Animated.View style={checkmarkStyle}>
          <Check size={13} color="#FFFFFF" strokeWidth={3} />
        </Animated.View>
      </Animated.View>
      <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
        {typeof label === 'string' ? (
          <Text
            className={cn('font-montserrat text-sm leading-5', 'text-[#2A2740] dark:text-white/85')}
          >
            {label}
            {required && (
              <Text style={{ color: colors.textAccent, fontFamily: FONTS.body }}> *</Text>
            )}
          </Text>
        ) : (
          <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 14, lineHeight: 20 }}>
            {label}
            {required && (
              <Text style={{ color: colors.textAccent, fontFamily: FONTS.body }}>{' *'}</Text>
            )}
          </Text>
        )}
      </View>
    </>
  )

  const rowStyle = {
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const,
    // The whole row toggles, so the whole row is the target. At SPACING.xs of
    // padding around a 20pt box it measured 29pt — on the checkout terms, the
    // registration consent and the AML cross-border consent, which are the
    // three most consequential checkboxes in the app.
    minHeight: INTERACTION.minTouchTarget,
    paddingVertical: SPACING.xs,
  }

  return (
    <View style={{ marginBottom: compact ? SPACING.xs + 2 : SPACING.sm + 2 }}>
      <Animated.View style={scaleStyle}>
        {accessibilityHidden ? (
          <View
            style={rowStyle}
            pointerEvents="none"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            {content}
          </View>
        ) : (
          <Pressable
            onPress={handlePress}
            onPressIn={onPressIn}
            onPressOut={onPressOut}
            style={rowStyle}
            disabled={disabled}
            testID={testID}
            accessibilityRole="checkbox"
            accessibilityState={{ checked, disabled: disabled ?? false }}
            accessibilityLabel={resolvedAccessibilityLabel}
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
          >
            {content}
          </Pressable>
        )}
      </Animated.View>
      {error ? (
        <Animated.Text
          entering={FadeInDown.duration(DURATION.fast)}
          exiting={FadeOut.duration(DURATION.fast)}
          style={{
            color: colors.textAccent,
            fontFamily: FONTS.body,

            fontSize: 12,
            marginTop: 2,
            marginLeft: SPACING.xl - 2,
          }}
        >
          {error}
        </Animated.Text>
      ) : null}
    </View>
  )
}
